/*
 Copyright (c) 2008-2010 Ricardo Quesada
 Copyright (c) 2011-2012 cocos2d-x.org
 Copyright (c) 2013-2016 Chukong Technologies Inc.
 Copyright (c) 2017-2020 Xiamen Yaji Software Co., Ltd.

 http://www.cocos2d-x.org

 Permission is hereby granted, free of charge, to any person obtaining a copy
 of this software and associated documentation files (the "Software"), to deal
 in the Software without restriction, including without limitation the rights
 to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 copies of the Software, and to permit persons to whom the Software is
 furnished to do so, subject to the following conditions:

 The above copyright notice and this permission notice shall be included in
 all copies or substantial portions of the Software.

 THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
 THE SOFTWARE.
*/

import '../data/class';
import { EventTarget } from '../event';
import { legacyCC } from '../global-exports';

export type TimeScaleObjectType = { scale: number };

export const defaultTimeScaleObject: TimeScaleObjectType = Object.freeze({
    scale: 1,
});

export class TimeScaleGroup extends EventTarget {
    public static instance: TimeScaleGroup;

    private _groupMap: Map<string, TimeScaleObjectType> = new Map();

    public get (name: string): number {
        if (!name) {
            return 1;
        }
        return this._groupMap.get(name)?.scale ?? defaultTimeScaleObject.scale;
    }

    public getObject (name: string): TimeScaleObjectType {
        if (!name) {
            return defaultTimeScaleObject;
        }
        const targetRecord = this._groupMap.get(name);
        if (!targetRecord) {
            this.set(name, 1);
        }
        return this._groupMap.get(name)!;
    }

    public set (name: string, timeScale: number): void {
        const record = this._groupMap.get(name);
        if (record) {
            record.scale = timeScale;
        } else {
            this._groupMap.set(name, { scale: timeScale });
        }
        this.emit('time-scale-changed', name, timeScale);
    }

    public resetAll (): void {
        for (const key of this._groupMap.keys()) {
            this.set(key, 1);
        }
    }

    public delete(name: string): void {
        this._groupMap.delete(name);
    }

    public clear(): void {
        this._groupMap.clear();
    }
}

// legacyCC.TimeScaleGroup = TimeScaleGroup;

/**
 * @en timeScaleGroup is the singleton TimeScaleGroup object.
 * @zh timeScaleGroup 是全局的管理時間流速分組物件。
 */
export const timeScaleGroup = (TimeScaleGroup.instance = legacyCC.timeScaleGroup = new TimeScaleGroup());
